import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Award, CalendarDays, CalendarOff, Cake, Check, ChevronDown, Plus, UserPlus } from "lucide-react";
import UserAvatar from "components/user-avatar/user-avatar";
import useIsPhone from "features/files/use-is-phone";
import { leaveCalendarLeaveTypeI18nKey } from "features/leave-calendar/leaveCalendar.constants";
import {
  EventResponse,
  NewsAbsentPerson,
  NewsBirthdaysList,
  NewsEvent,
  NewsEventsList,
  NewsNewColleague,
  NewsSidebar as NewsSidebarData,
} from "types/Post";
import { useEventAnswer } from "../hooks/useEventAnswer";
import { formatBirthday, formatMonthDay, formatTimeOfDay, useUiLanguage } from "../utils/newsDates";
import { EventDateBadge } from "./EventDateBadge";
import { CompanyPill } from "./UserNameWithCompany";

interface NewsSidebarProps {
  sidebar: NewsSidebarData;
  /** The company whose news page this is; people of other companies get a company label. */
  companyId: string;
  /** The viewer organises events, so an empty events block offers to create one. */
  canOrganize: boolean;
  /** Show the post that announces an event. */
  onOpenEvent: (postId: string) => void;
  onEventResponse: (postId: string, response: EventResponse | null) => Promise<void>;
  /** Open the composer with the event fields on. */
  onCreateEvent: () => void;
}

/**
 * Side blocks of the news page. A block with nobody to show is left out, except the events block,
 * which is always there and then says there are no events. Below `xl` the column goes above the
 * feed (the feed scrolls endlessly, so blocks under it would never be reached), two blocks side by side
 * from `md`; from `xl` it sits to the right. On a phone every block is folded to its title line.
 */
export function NewsSidebar({ sidebar, companyId, canOrganize, onOpenEvent, onEventResponse, onCreateEvent }: NewsSidebarProps) {
  const { absentToday, events, birthdays, newColleagues, anniversaries } = sidebar;
  const showAbsent = absentToday !== null && absentToday.people.length > 0;
  const showBirthdays = birthdays.people.length > 0;
  const showNewColleagues = newColleagues.people.length > 0;
  const showAnniversaries = anniversaries.people.length > 0;

  return (
    <aside
      className="order-first grid w-full items-start gap-4 max-md:gap-2 md:grid-cols-2 xl:order-last xl:sticky xl:top-4 xl:block xl:max-h-[calc(100dvh-6rem)] xl:w-96 xl:flex-shrink-0 xl:space-y-4 xl:self-start xl:overflow-y-auto"
      data-testid="news-sidebar"
    >
      {showAbsent && <AbsentTodayBlock list={absentToday} companyId={companyId} />}
      <EventsBlock
        list={events}
        companyId={companyId}
        canOrganize={canOrganize}
        onOpenEvent={onOpenEvent}
        onEventResponse={onEventResponse}
        onCreateEvent={onCreateEvent}
      />
      {showBirthdays && <BirthdaysBlock list={birthdays} companyId={companyId} />}
      {showNewColleagues && <NewColleaguesBlock list={newColleagues} companyId={companyId} />}
      {showAnniversaries && <AnniversariesBlock list={anniversaries} today={sidebar.today} companyId={companyId} />}
    </aside>
  );
}

function AbsentTodayBlock({
  list,
  companyId,
}: {
  list: NonNullable<NewsSidebarData["absentToday"]>;
  companyId: string;
}) {
  const { t } = useTranslation();
  const language = useUiLanguage();

  const absenceLabel = (person: NewsAbsentPerson): string => {
    if (person.reason === "trip") return [t("news_absent_trip"), person.place].filter(Boolean).join(" · ");
    if (!person.leaveType) return t("news_absent_leave");
    return t(leaveCalendarLeaveTypeI18nKey(person.leaveType), { defaultValue: person.leaveType });
  };

  const describe = (person: NewsAbsentPerson): string[] => {
    const until = t("news_until", { date: formatMonthDay(person.until, language) });
    return [absenceLabel(person), person.hours ? `${until} · ${person.hours.from}–${person.hours.to}` : until];
  };

  return (
    <SidebarCard icon={<CalendarOff className="h-4 w-4 text-gray-500" />} title={t("news_sidebar_absent_title")} count={list.total}>
      <ul className="space-y-3">
        {list.people.map((person) => (
          <PersonRow
            key={person.userId}
            companyId={companyId}
            userId={person.userId}
            name={person.name}
            lines={describe(person)}
          />
        ))}
      </ul>
      <MoreFooter count={list.total - list.people.length} to={`/app/${companyId}/leave-calendar`} />
    </SidebarCard>
  );
}

function EventsBlock({
  list,
  companyId,
  canOrganize,
  onOpenEvent,
  onEventResponse,
  onCreateEvent,
}: {
  list: NewsEventsList;
  companyId: string;
  canOrganize: boolean;
  onOpenEvent: (postId: string) => void;
  onEventResponse: (postId: string, response: EventResponse | null) => Promise<void>;
  onCreateEvent: () => void;
}) {
  const { t } = useTranslation();

  return (
    <SidebarCard
      icon={<CalendarDays className="h-4 w-4 text-gray-500" />}
      title={t("news_sidebar_events_title")}
      count={list.total}
    >
      {list.items.length === 0 ? (
        <>
          <p className="text-sm text-gray-600">{t("news_events_empty")}</p>
          {canOrganize && (
            <button
              type="button"
              onClick={onCreateEvent}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              <Plus className="h-4 w-4" />
              {t("news_events_create")}
            </button>
          )}
        </>
      ) : (
        <>
          <ul className="space-y-4">
            {list.items.map((event) => (
              <EventRow
                key={event.postId}
                event={event}
                isOtherCompany={event.companyId !== companyId}
                onOpen={() => onOpenEvent(event.postId)}
                onRespond={(response) => onEventResponse(event.postId, response)}
              />
            ))}
          </ul>
          <MoreFooter count={list.total - list.items.length} />
        </>
      )}
    </SidebarCard>
  );
}

interface EventRowProps {
  event: NewsEvent;
  /** The event belongs to another company than the page's, so it gets a company label. */
  isOtherCompany: boolean;
  onOpen: () => void;
  onRespond: (response: EventResponse | null) => Promise<void>;
}

/** The whole row opens the post; the "Going" button under it answers without opening anything. */
function EventRow({ event, isOtherCompany, onOpen, onRespond }: EventRowProps) {
  const { t } = useTranslation();
  const { isSaving, answer } = useEventAnswer(onRespond);
  // The server stops taking answers when the event starts, even though today's started events are still listed.
  const canAnswerGoing = event.myResponse === null && event.startsAt >= Date.now();
  const timeAndPlace = [formatTimeOfDay(event.startsAt), event.location].filter(Boolean).join(" · ");

  return (
    <li>
      <button type="button" onClick={onOpen} className="flex w-full items-start gap-3 text-left">
        <EventDateBadge startsAt={event.startsAt} />
        <span className="block min-w-0 flex-1">
          <span className="block break-words text-sm font-medium text-gray-900">{event.title}</span>
          <span className="block text-xs text-gray-600">{timeAndPlace}</span>
          <span className="block text-xs text-gray-600">{t("news_event_going_count", { count: event.goingCount })}</span>
          {isOtherCompany && event.companyName && (
            <CompanyPill name={event.companyName} className="mt-1 max-w-full truncate align-top" />
          )}
        </span>
      </button>
      {canAnswerGoing && (
        <button
          type="button"
          disabled={isSaving}
          onClick={() => answer("going")}
          className="ml-[68px] mt-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
        >
          {t("news_event_going")}
        </button>
      )}
      {event.myResponse === "going" && (
        <p className="ml-[68px] mt-2 flex items-center gap-1 text-xs font-medium text-blue-700">
          <Check className="h-3.5 w-3.5" />
          {t("news_event_going")}
        </p>
      )}
    </li>
  );
}

function NewColleaguesBlock({
  list,
  companyId,
}: {
  list: NewsSidebarData["newColleagues"];
  companyId: string;
}) {
  const { t } = useTranslation();
  const language = useUiLanguage();
  const startedLine = (person: NewsNewColleague) =>
    t("news_since", { date: formatMonthDay(person.startedOn, language) });

  return (
    <SidebarCard icon={<UserPlus className="h-4 w-4 text-gray-500" />} title={t("news_sidebar_new_colleagues_title")} count={list.total}>
      <ul className="space-y-3">
        {list.people.map((person) => (
          <PersonRow
            key={`${person.userId}-${person.companyId}`}
            companyId={companyId}
            userId={person.userId}
            name={person.name}
            lines={person.position ? [person.position, startedLine(person)] : [startedLine(person)]}
            otherCompanyName={person.companyId !== companyId ? person.companyName : null}
          />
        ))}
      </ul>
      <MoreFooter count={list.total - list.people.length} />
    </SidebarCard>
  );
}

function AnniversariesBlock({
  list,
  today,
  companyId,
}: {
  list: NewsSidebarData["anniversaries"];
  today: string;
  companyId: string;
}) {
  const { t } = useTranslation();
  const language = useUiLanguage();

  return (
    <SidebarCard icon={<Award className="h-4 w-4 text-gray-500" />} title={t("news_sidebar_anniversaries_title")} count={list.total}>
      <ul className="space-y-3">
        {list.people.map((person) => {
          const when = person.date === today ? t("news_today") : formatMonthDay(person.date, language);
          return (
            <PersonRow
              key={`${person.userId}-${person.companyId}`}
              companyId={companyId}
              userId={person.userId}
              name={person.name}
              lines={[`${t("news_years", { count: person.years })} · ${when}`]}
              otherCompanyName={person.companyId !== companyId ? person.companyName : null}
            />
          );
        })}
      </ul>
      <MoreFooter count={list.total - list.people.length} />
    </SidebarCard>
  );
}

function BirthdaysBlock({ list, companyId }: { list: NewsBirthdaysList; companyId: string }) {
  const { t } = useTranslation();
  const language = useUiLanguage();

  return (
    <SidebarCard icon={<Cake className="h-4 w-4 text-gray-500" />} title={t("news_sidebar_birthdays_title")} count={list.total}>
      <ul className="space-y-3">
        {list.people.map((person) => (
          <PersonRow
            key={`${person.userId}-${person.companyId}`}
            companyId={companyId}
            userId={person.userId}
            name={person.name}
            lines={[person.isToday ? t("news_today") : formatBirthday(person.month, person.day, language)]}
            isHighlighted={person.isToday}
            otherCompanyName={person.companyId !== companyId ? person.companyName : null}
          />
        ))}
      </ul>
      <MoreFooter count={list.total - list.people.length} />
    </SidebarCard>
  );
}

interface SidebarCardProps {
  icon: React.ReactNode;
  title: string;
  /** How many people or events the block holds; shown on the folded title line of a phone. */
  count: number;
  children: React.ReactNode;
}

/** On a phone every block starts as its title line, so the composer and the first post stay on the first screen. */
function SidebarCard({ icon, title, count, children }: SidebarCardProps) {
  const isPhone = useIsPhone();
  const [isOpen, setIsOpen] = useState(false);

  if (!isPhone) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900">
          {icon}
          {title}
        </h2>
        {children}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <h2 className="text-sm font-semibold text-gray-900">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-h-11 w-full items-center gap-2 px-4 text-left"
        >
          {icon}
          {title}
          {count > 0 && <span className="ml-auto text-xs font-normal text-gray-500">{count}</span>}
          <ChevronDown className={`h-4 w-4 text-gray-500 ${count > 0 ? "" : "ml-auto"} ${isOpen ? "rotate-180" : ""}`} />
        </button>
      </h2>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </section>
  );
}

interface PersonRowProps {
  companyId: string;
  userId: string;
  name: string;
  /** Text under the name, one line each. */
  lines: string[];
  /** Draws the lines under the name in the accent colour. */
  isHighlighted?: boolean;
  /** Set only for a person of another company than the page's. */
  otherCompanyName?: string | null;
}

function PersonRow({ companyId, userId, name, lines, isHighlighted = false, otherCompanyName }: PersonRowProps) {
  return (
    <li className="flex items-start gap-3">
      {/* No avatarFileId: other companies' people are shown by initials only. */}
      <UserAvatar companyId={companyId} user={{ id: userId, name }} size={32} rounded="full" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">{name}</p>
        {lines.map((line, index) => (
          <p key={index} className={isHighlighted ? "text-xs font-medium text-blue-700" : "text-xs text-gray-600"}>
            {line}
          </p>
        ))}
        {otherCompanyName && <CompanyPill name={otherCompanyName} className="mt-1 max-w-full truncate align-top" />}
      </div>
    </li>
  );
}

/** "+N more" under a list that shows fewer people than it has; a link when `to` is given. */
function MoreFooter({ count, to }: { count: number; to?: string }) {
  const { t } = useTranslation();
  if (count <= 0) return null;

  const label = t("news_more_people", { count });
  return to ? (
    <Link to={to} className="mt-1 block py-2 text-xs text-gray-500 hover:text-gray-900">
      {label}
    </Link>
  ) : (
    <p className="mt-3 text-xs text-gray-500">{label}</p>
  );
}
